/**
 * MetricsTab · Admin Console · Milestone A
 * ========================================
 * Top-level dashboard surfacing platform-wide and per-driver performance.
 * - 4 hero tiles (profits · tips · trip count · avg driving score)
 * - filterable per-driver table with date-range picker
 * - manual "Refresh" button that BYPASSES the cache via `?force=1&_=<ts>`
 * - 30s background poll that auto-clears on unmount (prevents leaks)
 *
 * Backend contract:
 *   GET /admin/metrics/aggregate?from=YYYY-MM-DD&to=YYYY-MM-DD
 *   GET /admin/metrics/by-driver?from=YYYY-MM-DD&to=YYYY-MM-DD
 *
 * Milestone B will add PDF export + tighter driving-score from telematics.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { RefreshCw, DollarSign, HandCoins, Activity, ShieldCheck, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";

import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";

import adminApi from "../../lib/adminApi";

const POLL_MS = 30000;

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function money(n) {
  return `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function scoreColor(score) {
  if (score >= 90) return "text-emerald-300 border-emerald-400/40";
  if (score >= 75) return "text-amber-300 border-amber-400/40";
  return "text-red-300 border-red-400/40";
}

function HeroTile({ label, value, sub, Icon, accent = "amber", testId }) {
  // v2.7.0 batch DO+++++++ · Premium glass + role-tinted backdrop.
  // Hover lift + intensified shadow handled by .stat-card-base.
  const tintClass = accent === "emerald" ? "stat-card-tint-mint"
    : accent === "blue" ? "stat-card-tint-sky"
    : accent === "amber" ? "stat-card-tint-amber"
    : "stat-card-tint-amber";
  const iconCls = accent === "emerald" ? "text-emerald-300 bg-emerald-400/15"
    : accent === "blue" ? "text-sky-300 bg-sky-400/15"
    : "text-amber-300 bg-amber-400/15";
  return (
    <Card data-testid={testId} className={`stat-card-base ${tintClass} p-5 group cursor-default`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold">{label}</div>
        <div className={`h-7 w-7 rounded-lg flex items-center justify-center ${iconCls} transition-transform duration-200 group-hover:scale-110`}>
          <Icon className="h-3.5 w-3.5"/>
        </div>
      </div>
      <div className="text-2xl font-extrabold tabular-nums text-zinc-50">{value}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-1">{sub}</div>}
    </Card>
  );
}

export default function MetricsTab() {
  const [from, setFrom] = useState(todayISO());
  const [to, setTo] = useState(todayISO());
  const [agg, setAgg] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // Mount-tracking ref prevents setState on unmount (memory-leak guard).
  const mountedRef = useRef(true);
  const pollRef = useRef(null);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = `?from=${from}&to=${to}&_=${Date.now()}`;
      const [a, b] = await Promise.all([
        adminApi.get(`/admin/metrics/aggregate${params}`, { headers: { "Cache-Control": "no-cache" } }),
        adminApi.get(`/admin/metrics/by-driver${params}`, { headers: { "Cache-Control": "no-cache" } }),
      ]);
      if (!mountedRef.current) return;
      setAgg(a.data);
      setRows(b.data.rows || []);
    } catch (e) {
      if (mountedRef.current && !silent) {
        toast.error(e?.response?.data?.detail || "Could not load metrics");
      }
    } finally {
      if (mountedRef.current && !silent) setLoading(false);
    }
  }, [from, to]);

  // Initial + every-30s background poll. Cleans interval on unmount AND when
  // the date window changes (closure-captured `fetchAll` would otherwise hit
  // stale dates).
  useEffect(() => {
    const mounted = mountedRef;
    mounted.current = true;
    fetchAll(false);
    pollRef.current = setInterval(() => fetchAll(true), POLL_MS);
    return () => {
      mounted.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchAll]);

  const filteredRows = rows.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-5" data-testid="metrics-tab-root">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight">Driver Metrics</h2>
          <p className="text-xs text-zinc-500 mt-1">
            Live performance feed · refreshes every 30s · America/Chicago bounds
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-[10px] uppercase text-zinc-400 mb-1 block">From</Label>
            <Input
              type="date"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="metrics-from"
              className="bg-zinc-950 border-zinc-700 text-zinc-200 h-9 w-36"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-zinc-400 mb-1 block">To</Label>
            <Input
              type="date"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
              data-testid="metrics-to"
              className="bg-zinc-950 border-zinc-700 text-zinc-200 h-9 w-36"
            />
          </div>
          <Button
            onClick={() => fetchAll(false)}
            disabled={loading}
            data-testid="metrics-refresh"
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold h-9"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin"/> : <RefreshCw className="h-4 w-4 mr-1"/>}
            Refresh Metrics
          </Button>
          <Button
            variant="outline"
            disabled={loading}
            data-testid="metrics-export-all"
            title="Download a platform-wide PDF for this date range"
            className="border-amber-400/40 text-amber-300 hover:bg-amber-400/10 h-9"
            onClick={async () => {
              try {
                const res = await adminApi.get(
                  `/admin/metrics/export.pdf?from=${from}&to=${to}`,
                  { responseType: "blob" }
                );
                const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `platform-metrics-${from}_${to}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 5000);
                toast.success("Platform PDF ready");
              } catch {
                toast.error("PDF download failed");
              }
            }}
          >
            <FileDown className="h-4 w-4 mr-1"/>Export All
          </Button>
        </div>
      </div>

      {/* Hero tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <HeroTile
          label="Profits Total"
          value={money(agg?.profits_total)}
          sub={`${agg?.trip_count ?? 0} trips · gross ${money(agg?.gross_total)}`}
          Icon={DollarSign}
          accent="amber"
          testId="metrics-tile-profits"
        />
        <HeroTile
          label="Tips Total"
          value={money(agg?.tips_total)}
          sub="Gratuity collected (cash + card)"
          Icon={HandCoins}
          accent="emerald"
          testId="metrics-tile-tips"
        />
        <HeroTile
          label="Active Drivers"
          value={agg?.active_driver_count ?? 0}
          sub={`Cash ${agg?.transaction_type?.cash ?? 0} · Card ${agg?.transaction_type?.card ?? 0} · Prepaid ${agg?.transaction_type?.prepaid ?? 0}`}
          Icon={Activity}
          accent="blue"
          testId="metrics-tile-active"
        />
        <HeroTile
          label="Avg Driving Score"
          value={`${agg?.average_driving_score ?? 0}`}
          sub="Telematics baseline · sensor-fed in Milestone B"
          Icon={ShieldCheck}
          accent="emerald"
          testId="metrics-tile-score"
        />
      </div>

      {/* Table */}
      <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="metrics-table-card">
        <div className="flex items-center justify-between mb-3 gap-3">
          <h3 className="text-sm font-bold">Per-Driver Performance ({filteredRows.length})</h3>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search driver name or email…"
            data-testid="metrics-search"
            className="bg-zinc-950 border-zinc-700 text-xs max-w-xs h-8"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <tr>
                <th className="text-left p-2">Driver</th>
                <th className="text-right p-2">Trips</th>
                <th className="text-right p-2">Today&rsquo;s Profits</th>
                <th className="text-right p-2">Total Tips</th>
                <th className="text-center p-2">Tx Mix</th>
                <th className="text-center p-2">Driving Score</th>
                <th className="text-right p-2 text-zinc-600">Export</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && !loading && (
                <tr><td colSpan={7} className="p-8 text-center text-zinc-500 text-xs">
                  No data for this window. Try expanding the date range.
                </td></tr>
              )}
              {filteredRows.map((r) => (
                <tr key={r.driver_id} data-testid={`metrics-row-${r.driver_id}`} className="border-b border-zinc-800 hover:bg-zinc-800/40">
                  <td className="p-2">
                    <div className="font-bold text-sm">{r.name}</div>
                    <div className="text-[10px] text-zinc-500">{r.email}</div>
                  </td>
                  <td className="p-2 text-right tabular-nums">{r.trip_count}</td>
                  <td className="p-2 text-right tabular-nums text-amber-300 font-bold" data-testid={`metrics-row-profits-${r.driver_id}`}>
                    {money(r.profits)}
                  </td>
                  <td className="p-2 text-right tabular-nums text-emerald-300" data-testid={`metrics-row-tips-${r.driver_id}`}>
                    {money(r.tips)}
                  </td>
                  <td className="p-2 text-center">
                    <div className="flex justify-center gap-1 text-[10px]">
                      {r.transaction_type?.cash > 0 && <Badge variant="outline" className="border-zinc-700 px-1 text-[9px]">Cash {r.transaction_type.cash}</Badge>}
                      {r.transaction_type?.card > 0 && <Badge variant="outline" className="border-blue-400/40 text-blue-300 px-1 text-[9px]">Card {r.transaction_type.card}</Badge>}
                      {r.transaction_type?.prepaid > 0 && <Badge variant="outline" className="border-amber-400/40 text-amber-300 px-1 text-[9px]">Prepaid {r.transaction_type.prepaid}</Badge>}
                    </div>
                  </td>
                  <td className="p-2 text-center">
                    <Badge variant="outline" className={`${scoreColor(r.driving_score)} text-xs tabular-nums`} data-testid={`metrics-row-score-${r.driver_id}`}>
                      {r.driving_score}
                    </Badge>
                  </td>
                  <td className="p-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-amber-400 text-[10px]"
                      title="Download per-driver PDF for this window"
                      data-testid={`metrics-pdf-${r.driver_id}`}
                      onClick={async () => {
                        try {
                          const res = await adminApi.get(
                            `/admin/metrics/export.pdf?from=${from}&to=${to}&driver_id=${r.driver_id}`,
                            { responseType: "blob" }
                          );
                          const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `metrics-${r.name || r.driver_id}-${from}_${to}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          setTimeout(() => URL.revokeObjectURL(url), 5000);
                          toast.success("PDF ready");
                        } catch (e) {
                          toast.error("PDF download failed");
                        }
                      }}
                    >
                      PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <p className="text-[10px] text-zinc-600 text-center" data-testid="metrics-as-of">
        Last updated · {agg?.as_of ? new Date(agg.as_of).toLocaleTimeString() : "—"}
      </p>
    </div>
  );
}
